import {
    Box,
    Button,
    Grid,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Info, Save, Settings } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect } from "react"
import { BotaoVoltar } from "./BotaoVoltar"
import { CadastroAplicativoActionsTypes } from "../context/cadastroAplicativoActions"
import { useCadastroAplicativoContext } from "../context/useCadastroAplicativoContext"
import type { CadastroAplicativoModel } from "../models/cadastroAplicativoModel"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    header: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(3) },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            borderRadius: 15,
            marginBottom: theme.spacing(2),
        },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    textField: {
        backgroundColor: "white",
        "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            "& fieldset": { borderColor: "#e0e0e0" },
            "&:hover fieldset": { borderColor: "#4caf50" },
            "&.Mui-focused fieldset": { borderColor: "#4caf50" },
        },
    },
    readOnlyField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            backgroundColor: "#f8f9fa",
        },
    },
    avisoSomenteLeitura: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        backgroundColor: "#fff8e1",
        border: "1px solid #ffe082",
        borderRadius: 12,
        padding: theme.spacing(1.5, 2),
        marginBottom: theme.spacing(3),
        color: "#8a6d3b",
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "center",
        marginTop: theme.spacing(4),
        [theme.breakpoints.down("sm")]: { marginTop: theme.spacing(3) },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        padding: theme.spacing(1.5, 4),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
        minWidth: "180px",
        "&:hover": {
            backgroundColor: "#45a049",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(76, 175, 80, 0.4)",
        },
        "&.Mui-disabled": {
            backgroundColor: "#c8e6c9",
            color: "white",
        },
        [theme.breakpoints.down("sm")]: {
            minWidth: "100%",
            padding: theme.spacing(2, 3),
        },
    },
}))

export function FormularioCadastroAplicativo() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, salvar, onEstabelecimentoChange, erroCarregamento } =
        useCadastroAplicativoContext()
    const { enqueueSnackbar } = useSnackbar()

    const somenteLeitura = dados.podeEditar === false
    const desabilitado = !!erroCarregamento || somenteLeitura

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const handleCampo = (
        campo: keyof CadastroAplicativoModel,
        valor: string | number | boolean | null
    ) => {
        dispatch({
            type: CadastroAplicativoActionsTypes.ATUALIZAR_CAMPO,
            payload: { campo, valor },
        })
    }

    const handleEstabelecimento = async (idEstabelecimento: number | null) => {
        await onEstabelecimentoChange(idEstabelecimento)
    }

    const handleSalvar = async () => {
        try {
            await salvar()
            alertStart("Cadastro salvo com sucesso!", "success")
        } catch (error) {
            alertStart((error as Error).message || "Erro ao salvar o cadastro", "error")
        }
    }

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <BotaoVoltar />
                <Box className={classes.header}>
                    <Typography variant="h4" className={classes.titleText}>
                        Cadastro do Aplicativo
                    </Typography>
                    <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                        URLs de acesso, FireBase, estabelecimento e situação do aplicativo
                    </Typography>
                </Box>

                {somenteLeitura && (
                    <Box className={classes.avisoSomenteLeitura}>
                        <Info />
                        <Typography variant="body2">
                            Este estabelecimento não possui um aplicativo próprio. As informações
                            exibidas são de leitura e não podem ser editadas aqui.
                        </Typography>
                    </Box>
                )}

                {/* Informações do App */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Settings className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Informações do App
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={isMobile ? 2 : 3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Código do App
                                </Typography>
                                <TextField
                                    className={classes.readOnlyField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.appNome}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Código Alternativo
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.appNomeAlternativo}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("appNomeAlternativo", value)
                                    }}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Package Name
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.packageName}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("packageName", value)
                                    }}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Categorias
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.categorias}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("categorias", value)
                                    }}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Link App Android
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.urlAppAndroid}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("urlAppAndroid", value)
                                    }}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Link App iPhone
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.urlAppIphone}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("urlAppIphone", value)
                                    }}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Código FireBase
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.codigoFireBase}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("codigoFireBase", value)
                                    }}
                                    disabled={desabilitado}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* URLs de Acesso */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Settings className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            URLs de Acesso
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={isMobile ? 2 : 3}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    URL de Acesso
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.urlAcesso}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("urlAcesso", value)
                                    }}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    URL de Acesso 2
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.urlAcesso2}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("urlAcesso2", value)
                                    }}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    URL de Acesso 3
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.urlAcesso3}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("urlAcesso3", value)
                                    }}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50", display: "flex", alignItems: "center" }}>
                                    Link Artes de Divulgação
                                    <Tooltip title="URL com materiais de divulgação do aplicativo" arrow>
                                        <Info style={{ fontSize: "1rem", color: "#7f8c8d", marginLeft: 4, cursor: "help" }} />
                                    </Tooltip>
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.urlArtesDivulgacao}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("urlArtesDivulgacao", value)
                                    }}
                                    disabled={desabilitado}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Configurações */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Settings className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Configurações
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={isMobile ? 2 : 3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Situação
                                </Typography>
                                <TextField
                                    select
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.situacao}
                                    onChange={e => {
                                        const value = Number(e.target.value)
                                        handleCampo("situacao", value)
                                    }}
                                    disabled={desabilitado}
                                >
                                    {dados.situacaoOptions.map(opt => (
                                        <MenuItem key={opt.id} value={opt.id}>
                                            {opt.descricao}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Ativo
                                </Typography>
                                <TextField
                                    select
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.ativo ? "sim" : "nao"}
                                    onChange={e => {
                                        handleCampo("ativo", e.target.value === "sim")
                                    }}
                                    disabled={desabilitado}
                                >
                                    <MenuItem value="sim">Sim</MenuItem>
                                    <MenuItem value="nao">Não</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Estabelecimento Principal
                                </Typography>
                                <TextField
                                    select
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.idEstabelecimentoPrincipal ?? ""}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleEstabelecimento(value !== "" ? Number(value) : null)
                                    }}
                                    disabled={desabilitado}
                                >
                                    <MenuItem value="">-Nenhum-</MenuItem>
                                    {dados.estabelecimentos.map(estab => (
                                        <MenuItem key={estab.id} value={estab.id}>
                                            {estab.nome}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Promoção Principal
                                </Typography>
                                <TextField
                                    select
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.idPromocaoPrincipal ?? ""}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo(
                                            "idPromocaoPrincipal",
                                            value !== "" ? Number(value) : null
                                        )
                                    }}
                                    disabled={desabilitado}
                                >
                                    <MenuItem value="">-Nenhuma-</MenuItem>
                                    {dados.promocoes.map(promo => (
                                        <MenuItem key={promo.id} value={promo.id}>
                                            {promo.premio}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Datas */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Settings className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Datas
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={isMobile ? 2 : 3}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Lançamento Previsto
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="date"
                                    value={dados.dataPrevisaoLancamento}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("dataPrevisaoLancamento", value)
                                    }}
                                    disabled={desabilitado}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 500, color: "#2c3e50" }}>
                                    Data do Lançamento
                                </Typography>
                                <TextField
                                    className={classes.textField}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="date"
                                    value={dados.dataLancamento}
                                    onChange={e => {
                                        const value = e.target.value
                                        handleCampo("dataLancamento", value)
                                    }}
                                    disabled={desabilitado}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                <Box className={classes.buttonContainer}>
                    <Button
                        className={classes.saveButton}
                        startIcon={!isMobile && <Save />}
                        onClick={handleSalvar}
                        disabled={desabilitado}
                        size={isMobile ? "large" : "medium"}
                    >
                        {isMobile ? "💾 Salvar Cadastro" : "Salvar Cadastro"}
                    </Button>
                </Box>
            </div>
        </div>
    )
}
